import { useRef, useState } from "react";

import { useGame } from "../game/GameContext";
import type { Intent } from "../game/api";

export default function InputForm() {
  // Issue #493: the form now carries a do/say intent toggle and an
  // optional target-NPC dropdown that appears when ``intent === "say"``
  // and the room has present NPCs. ``submitAction`` forwards both
  // through to the wire as ``intent`` + ``target_entity_id``.
  const { busy, submitAction, presentNpcs } = useGame();
  const [value, setValue] = useState("");
  const [intent, setIntent] = useState<Intent>("do");
  // Empty string = "(whoever is listening)" — let the engine pick the
  // first present NPC. A non-empty string is an explicit entity_id.
  const [targetEntityId, setTargetEntityId] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const showNpcPicker = intent === "say" && presentNpcs.length > 0;

  // Issue #605: per-mode placeholder so the input cues the player to
  // what their next submit will do. Examine asks for an entity / focus
  // target since the engine resolves the rest from the room context.
  const placeholder =
    intent === "say"
      ? "What do you say?"
      : intent === "examine"
      ? "What do you examine?"
      : "What do you do?";

  return (
    <form
      id="input-form-wrapper"
      onSubmit={(e) => {
        e.preventDefault();
        const action = value.trim();
        if (!action) return;
        // Snapshot intent/target at submit time. We deliberately leave
        // the toggle as-is after submit — players who switch to "say"
        // (or "examine", #605) usually want to send a few lines in a
        // row. The input box still clears so the next utterance is
        // fresh.
        const opts =
          intent === "say"
            ? { intent, targetEntityId: targetEntityId || undefined }
            : { intent };
        // Issue #536: clear the input synchronously on send so the
        // player's typed text doesn't sit visible for the full multi-
        // second turn stream. We pass the captured ``action`` into
        // ``submitAction`` (not ``value``), so the ``setValue("")``
        // call cannot race the submit — React state updates are
        // batched/async but ``action`` is a plain local already.
        setValue("");
        inputRef.current?.focus();
        void submitAction(action, opts);
      }}
    >
      <div className="intent-toggle" role="group" aria-label="Intent">
        <div className="intent-toggle-pills" role="tablist">
          <button
            type="button"
            role="tab"
            aria-pressed={intent === "do"}
            disabled={busy}
            onClick={() => setIntent("do")}
            className="icon-label"
          >
            {/* Issue #542: hand / pointing-finger glyph for "Do".
                ``aria-hidden`` so the visible "Do" label remains the
                accessible name. */}
            <svg
              aria-hidden="true"
              width="1em"
              height="1em"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 11V5.5a1.5 1.5 0 0 1 3 0V11" />
              <path d="M12 11V4.5a1.5 1.5 0 0 1 3 0V11" />
              <path d="M15 11V6.5a1.5 1.5 0 0 1 3 0V14" />
              <path d="M9 11V8.5a1.5 1.5 0 0 0-3 0v6.5a7 7 0 0 0 12 5l1.5-2.5a3 3 0 0 0-.5-3.5" />
            </svg>
            Do
          </button>
          <button
            type="button"
            role="tab"
            aria-pressed={intent === "say"}
            disabled={busy}
            onClick={() => setIntent("say")}
            className="icon-label"
          >
            {/* Issue #542: speech-bubble glyph for "Say".
                ``aria-hidden`` so the visible "Say" label remains the
                accessible name. */}
            <svg
              aria-hidden="true"
              width="1em"
              height="1em"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            Say
          </button>
          {/* Issue #605: examine is its own sticky mode alongside Do/Say.
              When selected, submit sends ``intent="examine"`` and the
              engine routes the turn through the read-only investigation
              path (entity-focused on_read fan-in per #592) instead of
              the action router. Matches Do/Say's role=tab + aria-pressed
              semantics so the toggle group reads as three peer tabs. */}
          <button
            type="button"
            role="tab"
            aria-pressed={intent === "examine"}
            disabled={busy}
            onClick={() => setIntent("examine")}
            className="icon-label"
          >
            {/* Magnifying-glass glyph — Lucide-style stroke vocabulary
                matching the Do/Say icons. ``aria-hidden`` so the visible
                "Examine" label remains the accessible name. */}
            <svg
              aria-hidden="true"
              width="1em"
              height="1em"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            Examine
          </button>
        </div>
        {showNpcPicker && (
          <select
            className="intent-target-select"
            aria-label="Say to"
            disabled={busy}
            value={targetEntityId}
            onChange={(e) => setTargetEntityId(e.target.value)}
          >
            <option value="">(whoever is listening)</option>
            {presentNpcs.map((npc) => (
              <option key={npc.entity_id} value={npc.entity_id}>
                {npc.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div id="input-form">
        <input
          ref={inputRef}
          id="input"
          type="text"
          placeholder={placeholder}
          autoComplete="off"
          autoFocus
          disabled={busy}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <button type="submit" id="submit-btn" disabled={busy}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </form>
  );
}
